import {
  OdProtosCasaData_Result,
  OdProtosSellCommon_Product,
  OdProtosSellReceptionData_SellerInput_AddressVerifiedStatus,
} from 'src/__generated__/globalTypes';
import {
  GetSellerInputQuery_reception_getSellerInput_sellerInput_address,
  GetSellerInputQuery_reception_getSellerInput_sellerInput_homeValue,
  GetSellerInputQuery_reception_getSellerInput_sellerInput_partnershipReferrals,
  GetSellerInputQuery_reception_getSellerInput_sellerInput_productOfferings,
} from 'src/api/queries/__generated__/GetSellerInputQuery';
import { PartnershipReferral, Product, ProductOffering } from 'src/declarations';
import { BWOD_MARKETS } from 'src/helpers/markets';

/**
 * These are helper functions for converting the getSellerInput gql response
 * into useful flags and values
 */

/**
 * Returns if the seller input's region is in Buy with Opendoor (BwOD)
 */
export const isInBwODMarket = (region: string): boolean => {
  return BWOD_MARKETS.some((marketObj) => region === marketObj.value);
};

const PARTNERSHIP_DENIALS = [
  'address',
  'year_built',
  'lot_sq_ft',
  'gated_community',
  'shared_entrance',
  'age_restricted_community',
  'foundation',
  'septic',
  'private_pool',
  'livestock',
  'flood_zone',
  'fire_damage',
  'well_water',
  'asbestos_siding',
  'underground_oil_tank',
  'leased_solar_panels',
];

const toProduct = (protoProduct: OdProtosSellCommon_Product): Product | null => {
  switch (protoProduct) {
    case OdProtosSellCommon_Product.AGENT_REFERRAL:
      return 'agent_referral';
    case OdProtosSellCommon_Product.REIT:
      return 'reit';
    case OdProtosSellCommon_Product.SELL_WITH_EXCLUSIVES:
      return 'sell_with_exclusives';
    case OdProtosSellCommon_Product.OPENDOOR_DIRECT:
      return 'opendoor_direct';
    case OdProtosSellCommon_Product.BUY_WITH_OPENDOOR:
    case OdProtosSellCommon_Product.LIST_WITH_OPENDOOR:
    case OdProtosSellCommon_Product.OPENDOOR_BACKED_OFFERS:
    case OdProtosSellCommon_Product.UNKNOWN_PRODUCT:
    default:
      return null;
  }
};

// helper type predicate to make typescript happy that our array has filtered out all nulls
function notNull<T>(value: T | null): value is T {
  return value !== null;
}

/**
 * Returns the list of potential products the seller inputs are eligible for
 */
export const getEligibleProducts = (
  leadState: string,
  productOfferings: Array<GetSellerInputQuery_reception_getSellerInput_sellerInput_productOfferings>,
): Array<Product> => {
  const eligibleProducts = productOfferings
    .filter((productOffering) => !productOffering.deniedAt)
    .map((productOffering) => toProduct(productOffering.product))
    .filter(notNull); // remove null products

  // HACK: Leads created in the seller app use Web and not Reception, meaning they'll never
  // have any product offerings at all.
  // We should remove this once the mobile app seller flow is moved onto Reception.
  if (productOfferings.length === 0 && leadState !== 'denied') {
    eligibleProducts.push('opendoor_direct');
  }
  return eligibleProducts;
};

const getOpendoorDirectProductOffering = (
  productOfferings: Array<GetSellerInputQuery_reception_getSellerInput_sellerInput_productOfferings>,
) => {
  return productOfferings.find(
    ({ product }) => product === OdProtosSellCommon_Product.OPENDOOR_DIRECT,
  );
};

export const getLeadState = (
  addressVerifiedStatus: OdProtosSellReceptionData_SellerInput_AddressVerifiedStatus,
  isListed: boolean,
  productOfferings: Array<GetSellerInputQuery_reception_getSellerInput_sellerInput_productOfferings>,
  address: GetSellerInputQuery_reception_getSellerInput_sellerInput_address | null,
): string => {
  const needsToVerifyAddress =
    !addressVerifiedStatus &&
    addressVerifiedStatus !==
      OdProtosSellReceptionData_SellerInput_AddressVerifiedStatus.NOT_VERIFIED;

  const isMissingUnitNumber =
    address?.result?.includes(OdProtosCasaData_Result.ADDRESS_MISSING_SUB_PREMISE) ?? false;

  const isInvalidUnitNumber =
    address?.result?.includes(OdProtosCasaData_Result.ADDRESS_INVALID_SUB_PREMISE) ?? false;

  const needsToVerifyUnitNumber =
    (isMissingUnitNumber || isInvalidUnitNumber) &&
    addressVerifiedStatus !==
      OdProtosSellReceptionData_SellerInput_AddressVerifiedStatus.CONFIRMED_VERIFIED;

  const odDirectProductOffering = getOpendoorDirectProductOffering(productOfferings);

  if (needsToVerifyAddress || needsToVerifyUnitNumber) {
    return 'verifying_address';
  } else if (odDirectProductOffering?.deniedAt) {
    // FIXME:
    // previous code used getDenialReason which did not contain all of our denial reasons
    // need to make sure it's okay to do this here
    return 'denied';
  } else if (isListed) {
    return 'verifying_listing';
  } else if (odDirectProductOffering?.qualifiedAt) {
    return 'needs_attention';
  }

  return 'inputting_answers';
};

export const getHomeBuilderIdentifier = (
  partnerships: Array<
    | GetSellerInputQuery_reception_getSellerInput_sellerInput_partnershipReferrals
    | PartnershipReferral
  >,
): string | undefined => {
  const homebuilder = partnerships?.find((referral) => referral.type === 'home_builder');
  if (!homebuilder || !homebuilder.identifier) {
    return;
  }
  return homebuilder.identifier;
};

export const getOrePartnerIdentifier = (
  partnerships: Array<
    | GetSellerInputQuery_reception_getSellerInput_sellerInput_partnershipReferrals
    | PartnershipReferral
  >,
): string | undefined => {
  const orePartner = partnerships?.find((referral) => referral.type === 'online_real_estate');
  if (!orePartner || !orePartner.identifier) {
    return;
  }
  return orePartner.identifier;
};

export const getIsInPartnershipExpansionDenial = (
  productOfferings: Array<
    GetSellerInputQuery_reception_getSellerInput_sellerInput_productOfferings | ProductOffering
  >,
  denialPartnerIdentifier: string,
): boolean => {
  if (denialPartnerIdentifier === '') {
    return false;
  }
  for (const productOffering of productOfferings) {
    if (productOffering.product?.toLowerCase() === 'opendoor_direct') {
      for (const denialReason of productOffering.denialReasons) {
        if (denialReason && PARTNERSHIP_DENIALS.includes(denialReason?.toLowerCase())) {
          return true;
        }
      }
    }
  }

  return false;
};

export const getHasHomebuilderSalesAssociate = (
  partnerships: Array<GetSellerInputQuery_reception_getSellerInput_sellerInput_partnershipReferrals>,
): boolean => partnerships.some(({ type, userId }) => type === 'home_builder' && !!userId);

export const getHomeValuationEstimate = (
  homeValue: GetSellerInputQuery_reception_getSellerInput_sellerInput_homeValue | null,
) => {
  if (homeValue && homeValue.headlineRangeLower && homeValue.valuationCeilingCents) {
    return {
      min: homeValue.headlineRangeLower.value,
      max: homeValue.valuationCeilingCents,
      variance: homeValue.variance ?? 0,
    };
  }
};
